import React, { useState, ReactElement, useRef, useEffect } from 'react';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { createPortal } from 'react-dom';

type InfoTooltipProps = {
    text: string;
    iconSize?: number;
    variant?: 'primary' | 'secondary' | 'neutral';
};

const InfoTooltip: React.FC<InfoTooltipProps> = ({ 
    text, 
    iconSize = 18,
    variant = 'primary'
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const iconRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const handleMouseEnter = () => {
        setIsVisible(true);
    };

    const handleMouseLeave = () => {
        setIsVisible(false);
    };

    const handleTouchStart = () => {
        setIsVisible(!isVisible);
    };

    useEffect(() => {
        if (isVisible && iconRef.current && tooltipRef.current) {
            const iconRect = iconRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Calculate initial position (centered above the icon)
            let top = iconRect.top - tooltipRect.height - 8;
            let left = iconRect.left + (iconRect.width - tooltipRect.width) / 2;

            // Check if tooltip would go off the top
            if (top < 0) {
                top = iconRect.bottom + 8; // Show below instead
            }

            // Check if tooltip would go off the right
            if (left + tooltipRect.width > viewportWidth) {
                left = viewportWidth - tooltipRect.width;
            }

            // Check if tooltip would go off the left
            if (left < 0) {
                left = 0;
            }

            setTooltipPosition({ top, left });
        }
    }, [isVisible]);

    const renderTooltip = () => {
        if (!isVisible) return null;

        return createPortal(
            <div
                ref={tooltipRef}
                className={`fixed z-50 w-64 p-2 caption1 text-white rounded-md shadow-lg ${
                    variant === 'primary' ? 'bg-primary' : 
                    variant === 'secondary' ? 'bg-secondary-300' : 
                    'bg-neutral-700'
                }`}
                style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                }}
            >
                {text}
                {/* <div 
                    className="absolute w-2 h-2 bg-neutral-800 transform rotate-45"
                    style={{
                        top: tooltipPosition.top > iconRef.current?.getBoundingClientRect().top! ? '-4px' : 'auto',
                        bottom: tooltipPosition.top > iconRef.current?.getBoundingClientRect().top! ? 'auto' : '-4px',
                        left: `${tooltipPosition.left + (tooltipRef.current?.offsetWidth! / 2) - 4}px`
                    }}
                /> */}
            </div>,
            document.body
        );
    };

    return (
        <>
            <div
                ref={iconRef}
                className="relative inline-block"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
            >
                <IoIosInformationCircleOutline 
                    size={iconSize}
                    className={`cursor-help ${
                        variant === 'primary' ? 'text-primary' :
                        variant === 'secondary' ? 'text-secondary-300' :
                        'text-neutral-700'
                    }`}
                />
            </div>
            {renderTooltip()}
        </>
    );
};

export default InfoTooltip;